import type { SitePhotoList, SiteReportSnapshot } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { Image as ImageIcon, Video } from 'lucide-react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { useMe } from '../../lib/session';
import { EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Page, PageHeader, Panel } from '../../ui/layout';

type Search = { project?: string; activity?: string; sheet?: string; remark?: string };

/** Адрес файла фото отчёта: сервер проверяет право просмотра площадки и карантин, затем отдаёт ссылку хранилища. */
export const sitePhotoFileUrl = (reportId: string, clientPhotoId: string, revision?: number) =>
  `/api/v1/site/reports/${reportId}/photos/${clientPhotoId}/file${revision ? `?revision=${revision}` : ''}`;

/** Страница «Фото площадки» с фильтром: по работе графика, листу ПСД или замечанию. */
export const sitePhotosLink = (
  projectId: string,
  by: { activity?: string; sheet?: string; remark?: string } = {},
) => ({ to: '/site/photos' as const, search: { project: projectId, ...by } });

const dayLabel = (day: string) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });

function OpenLink({ href, video }: { href: string; video: boolean }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex min-h-11 items-center gap-2 rounded-md border border-line px-3 text-sm font-medium text-accent hover:bg-tile"
    >
      {video ? <Video className="size-4" aria-hidden /> : <ImageIcon className="size-4" aria-hidden />}
      {video ? 'Открыть видео' : 'Открыть фото'}
    </a>
  );
}

/** Фото и видео одной работы в отчёте смены (для проверяющего): подпись, источник, место, ссылка на файл. */
export function ReportPhotos({ report, lineId }: { report: SiteReportSnapshot; lineId: string }) {
  const photos = report.draft.photos.filter((p) => p.lineId === lineId);
  if (!photos.length) return null;
  return (
    <div className="mt-4">
      <h3 className="text-sm font-semibold">Фото и видео · {photos.length}</h3>
      <ul className="mt-2 grid gap-3">
        {photos.map((p) => (
          <li key={p.clientPhotoId} className="flex flex-col gap-2 text-sm sm:flex-row sm:items-center">
            {p.fileId ? (
              <OpenLink
                href={sitePhotoFileUrl(report.id, p.clientPhotoId, report.revision)}
                video={p.kind === 'video'}
              />
            ) : (
              <span className="text-ink-2">Файл ещё не загружен с телефона</span>
            )}
            <span className="min-w-0 break-words">
              {p.caption || 'Без подписи'}
              <span className="text-ink-2">
                {p.source === 'gallery' ? ' · из галереи, время и место не подтверждены' : ' · снято камерой'}
                {p.locationConsent && p.location ? ' · место сохранено с согласия' : ''}
                {p.sheetId ? ' · есть лист ПСД' : ''}
                {p.remarkId ? ' · есть замечание' : ''}
              </span>
            </span>
          </li>
        ))}
      </ul>
      <Link
        {...sitePhotosLink(report.draft.projectId, {
          activity: report.draft.lines.find((l) => l.id === lineId)?.activityId,
        })}
        className="mt-2 inline-flex min-h-11 items-center text-sm text-accent underline"
      >
        Все фото по этой работе
      </Link>
    </div>
  );
}

/** Фото площадки по работе графика, листу ПСД или замечанию (ПР-03): ссылка из карточек этих разделов. */
export function SitePhotosPage() {
  const me = useMe();
  const search = useSearch({ strict: false }) as Search;
  useCrumbs([{ label: 'Площадка', to: '/site' }, { label: 'Фото площадки' }]);
  const filter = search.activity
    ? { activityId: search.activity }
    : search.sheet
      ? { sheetId: search.sheet }
      : search.remark
        ? { remarkId: search.remark }
        : {};
  const q = useQuery({
    queryKey: ['site', me.tenant?.id, 'photos', search.project, filter],
    queryFn: () => api<SitePhotoList>(`/site/projects/${search.project}/photos`, { query: filter }),
    enabled: Boolean(me.tenant?.staff && search.project),
  });
  const what = search.activity
    ? 'по работе графика'
    : search.sheet
      ? 'по листу ПСД'
      : search.remark
        ? 'по замечанию'
        : 'по проекту';
  return (
    <Page>
      <PageHeader title="Фото площадки" subtitle={`Фото и видео из отчётов смены ${what}`} />
      {!me.tenant?.staff ? (
        <ErrorBox error={new Error('Площадка доступна сотрудникам компании')} />
      ) : !search.project ? (
        <EmptyState title="Проект не выбран">
          Откройте фото из карточки работы, листа ПСД, замечания или отчёта смены.
        </EmptyState>
      ) : q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : !q.data.items.length ? (
        <EmptyState title="Фото пока нет">Прораб ещё не прикладывал фото с такой привязкой.</EmptyState>
      ) : (
        <Panel title={`Фото и видео: ${q.data.items.length}`}>
          <ul className="divide-y divide-line">
            {q.data.items.map((p) => (
              <li key={p.id} className="flex flex-col gap-2 py-3">
                <div className="min-w-0 break-words text-[15px] font-medium text-ink">
                  {p.caption || (p.kind === 'video' ? 'Видео без подписи' : 'Фото без подписи')}
                </div>
                <div className="break-words text-[13px] text-ink-2">
                  {dayLabel(p.reportDay)} · {p.authorName} · {p.activityName}
                  {p.sheetLabel ? ` · лист ${p.sheetLabel}` : ''}
                  {p.remarkNumber ? ` · замечание ${p.remarkNumber}` : ''}
                  {p.source === 'gallery' ? ' · из галереи' : ''}
                  {p.locationSaved ? ' · место сохранено с согласия' : ''}
                  {p.reportState === 'draft' ? ' · черновик' : ''}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {p.fileReady ? (
                    <OpenLink
                      href={sitePhotoFileUrl(p.reportId, p.clientPhotoId)}
                      video={p.kind === 'video'}
                    />
                  ) : (
                    <span className="text-[13px] text-ink-2">Файл ещё загружается или проверяется</span>
                  )}
                  {p.reportState !== 'draft' ? (
                    <Link
                      to="/site/review"
                      search={{ project: search.project, report: p.reportId }}
                      className="inline-flex min-h-11 items-center text-sm text-accent underline"
                    >
                      Отчёт смены
                    </Link>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </Page>
  );
}
